import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiPlus,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiImage,
  FiSettings,
  FiUser,
  FiType,
  FiUpload,
} from "react-icons/fi";
import "../styles/BlogForm.css";

const API_BASE_URL = "https://jobs.mpdatahub.com/api";
const CATEGORIES = [
  "Recruitment",
  "Culture",
  "Compliance",
  "Performance",
  "Well-being",
  "DEI",
];

export default function BlogForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "Culture",
    author: "",
    authorRole: "Contributor",
    image: null, // Changed from string to file object
    imagePreview: "", // For preview
    tags: [],
    featured: false,
    readTime: "5 min read",
  });

  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingBlog, setFetchingBlog] = useState(false);
  const [toast, setToast] = useState(null);
  const [imageError, setImageError] = useState("");

  // Character limits
  const EXCERPT_LIMIT = 200;

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // In the fetchBlog function, add this tags parsing logic:

  const fetchBlog = useCallback(async () => {
    try {
      setFetchingBlog(true);
      const response = await fetch(`${API_BASE_URL}/blogs/${id}`);
      if (!response.ok) throw new Error("Blog not found");
      const blog = await response.json();

      // Parse tags if it's a string, otherwise use array or empty array
      let parsedTags = blog.tags || [];
      if (typeof parsedTags === "string") {
        try {
          parsedTags = JSON.parse(parsedTags);
        } catch (e) {
          parsedTags = parsedTags ? [parsedTags] : [];
        }
      }
      if (!Array.isArray(parsedTags)) {
        parsedTags = [];
      }

      // Transform the data to match form state
      setFormData({
        title: blog.title,
        excerpt: blog.excerpt,
        content: blog.content,
        category: blog.category,
        author: blog.author || "",
        authorRole: blog.authorRole || "Contributor",
        image: null, // Can't pre-populate file input
        imagePreview: blog.image_url, // Show existing image preview
        tags: parsedTags, // Use parsed tags array
        featured:
          blog.featured === true ||
          blog.featured === 1 ||
          blog.featured === "1",
        readTime: blog.readTime || "5 min read",
      });
    } catch (err) {
      showToast("Failed to load blog data", "error");
    } finally {
      setFetchingBlog(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) fetchBlog();
  }, [isEditing, fetchBlog]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageError("");

    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setImageError("Only JPG, JPEG, PNG, and WEBP images are allowed");
        return;
      }

      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        setImageError("Image size must be less than 2MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const handleTagOps = (e, op, tag = null) => {
    if (op === "add" && tagInput.trim()) {
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData((p) => ({ ...p, tags: [...p.tags, tagInput.trim()] }));
      }
      setTagInput("");
    } else if (op === "remove") {
      setFormData((p) => ({ ...p, tags: p.tags.filter((t) => t !== tag) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.title || !formData.excerpt || !formData.content) {
      showToast("Please fill in all required fields", "error");
      return;
    }

    // Validate image for new posts
    if (!isEditing && !formData.image) {
      showToast("Please select a cover image", "error");
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("excerpt", formData.excerpt);
      formDataToSend.append("content", formData.content);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("authorRole", formData.authorRole);
      formDataToSend.append("readTime", formData.readTime);

      if (formData.author) {
        formDataToSend.append("author", formData.author);
      }

      if (formData.featured) {
        formDataToSend.append("featured", "1");
      }

      if (formData.tags.length > 0) {
        formDataToSend.append("tags", JSON.stringify(formData.tags));
      }

      // Append image if it's a new file
      if (formData.image instanceof File) {
        formDataToSend.append("image", formData.image);
      }

      let url, method;
      if (isEditing) {
        url = `${API_BASE_URL}/blogs-update/${id}`;
        method = "POST"; // Using POST as per your API endpoint
      } else {
        url = `${API_BASE_URL}/blogs-store`;
        method = "POST";
      }

      const response = await fetch(url, {
        method: method,
        body: formDataToSend, // Don't set Content-Type header, let browser set it with boundary
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Operation failed");
      }

      const result = await response.json();
      showToast(
        isEditing
          ? "Post updated successfully!"
          : "Post published successfully!",
      );

      setTimeout(() => navigate("/admin"), 1500);
    } catch (err) {
      console.error("Error:", err);
      showToast(err.message || "Operation failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetchingBlog)
    return (
      <div className="loader-screen">
        <div className="spinner"></div>
      </div>
    );

  return (
    <div className="admin-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`toast-popup ${toast.type}`}>
          {toast.type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
          {toast.msg}
        </div>
      )}

      {/* Floating Action Header */}
      <header className="form-sticky-header">
        <div className="header-content">
          <button className="icon-btn-back" onClick={() => navigate("/admin")}>
            <FiArrowLeft /> <span>Back</span>
          </button>
          <div className="header-title">
            <h1>{isEditing ? "Edit Post" : "New Post"}</h1>
            <span className="status-badge">
              {isEditing ? "Editing" : "New Draft"}
            </span>
          </div>
          <button
            form="blog-main-form"
            type="submit"
            className={`btn-publish ${loading ? "loading" : ""}`}
            disabled={loading}
          >
            {loading ? "Saving..." : isEditing ? "Update" : "Publish"}
          </button>
        </div>
      </header>

      <form
        id="blog-main-form"
        onSubmit={handleSubmit}
        className="modern-form-layout"
      >
        <div className="main-content-area">
          {/* Section: Content */}
          <section className="form-card-new">
            <div className="section-title">
              <FiType /> Editor Content
            </div>

            <div className="input-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="The future of HR management..."
                required
              />
            </div>

            <div className="input-group">
              <label>Excerpt *</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                maxLength={EXCERPT_LIMIT}
                placeholder="A short summary for the card view..."
                rows="2"
                required
              />
              <div
                className={`char-counter ${formData.excerpt.length > 180 ? "warning" : ""}`}
              >
                {formData.excerpt.length} / {EXCERPT_LIMIT}
              </div>
            </div>

            <div className="input-group">
              <label>Body Content *</label>
              <textarea
                name="content"
                className="content-textarea"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Start writing your story..."
                rows="10"
                required
              />
            </div>
          </section>

          {/* Section: Media */}
          <section className="form-card-new">
            <div className="section-title">
              <FiImage /> Cover Media {!isEditing && "*"}
            </div>
            <div className="input-group">
              <div className="file-upload-wrapper">
                <label htmlFor="image-upload" className="file-upload-label">
                  <FiUpload />{" "}
                  {formData.imagePreview ? "Change Image" : "Upload Image"}
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
                <small className="file-hint">
                  JPG, JPEG, PNG, WEBP only. Max 2MB
                </small>
              </div>
              {imageError && <div className="error-message">{imageError}</div>}
            </div>
            {formData.imagePreview && (
              <div className="preview-container">
                <img src={formData.imagePreview} alt="Cover Preview" />
                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      imagePreview: "",
                      image: null,
                    }));
                    setImageError("");
                  }}
                  className="remove-img"
                >
                  <FiX />
                </button>
              </div>
            )}
          </section>
        </div>

        <aside className="sidebar-area">
          {/* Category & Tags */}
          <section className="form-card-new">
            <div className="section-title">
              <FiSettings /> Taxonomy
            </div>

            <div className="input-group">
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Tags</label>
              <div className="tag-input-wrapper">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    (e.preventDefault(), handleTagOps(null, "add"))
                  }
                  placeholder="Press Enter to add"
                />
                <button type="button" onClick={() => handleTagOps(null, "add")}>
                  <FiPlus />
                </button>
              </div>
              <div className="tags-container">
                {formData.tags.map((tag) => (
                  <span key={tag} className="modern-tag">
                    {tag}{" "}
                    <FiX onClick={() => handleTagOps(null, "remove", tag)} />
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Author Details */}
          <section className="form-card-new">
            <div className="section-title">
              <FiUser /> Metadata
            </div>
            <div className="input-group">
              <label>Author Name</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                placeholder="Author name (optional)"
              />
            </div>
            <div className="input-group">
              <label>Author Role</label>
              <input
                type="text"
                name="authorRole"
                value={formData.authorRole}
                onChange={handleInputChange}
                placeholder="e.g., HR Specialist"
              />
            </div>
            <div className="input-group">
              <label>Read Time</label>
              <input
                type="text"
                name="readTime"
                value={formData.readTime}
                onChange={handleInputChange}
                placeholder="e.g., 5 min read"
              />
            </div>
            <label className="featured-toggle">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleInputChange}
              />
              <div className="toggle-slider"></div>
              <span>Mark as Featured Post</span>
            </label>
          </section>
        </aside>
      </form>
    </div>
  );
}
